import * as Sieve from '~/util/sieve/sieve';
import * as types from '../sieve/types';
import { axios } from '~/util/http/axioshttp';
import { useQuery, type QueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { ProblemDetails } from '~/util/http/types';
import type { AxiosError } from 'axios';
import { GetSearchProps, type SearchOptionType, type SearchTypeToModel } from '@generated';
import type { SieveFilterModel } from '../sieve/filter';

export interface useSearchProps<K extends SearchOptionType>
	extends types.SieveSearchModel,
		Omit<UseQueryOptions<{ collection: SearchTypeToModel[K][]; count: number }>, 'queryKey' | 'query'> {
	searchType: K;
	onSuccess?: (data: { collection: SearchTypeToModel[K][]; count: number }) => void;
}
interface useSearchResult<T> {
	data: T[];
	count: number;
	isLoading: boolean;
	isError: boolean;
	error: AxiosError<ProblemDetails, T> | null;
	refetch: () => void;
	isFetching: boolean;
	sortColumn?: string | null;
	sortDirection?: string | null;
}

export function invalidateSearchQuery(queryClient: QueryClient, searchType: SearchOptionType) {
	const query = GetSearchProps(searchType);
	queryClient.invalidateQueries({
		queryKey: [query.endpoint, 'search']
	});
}

export function getSearchQueryKey(searchType: SearchOptionType) {
	const query = GetSearchProps(searchType);
	return [query.endpoint, 'search'];
}

export function getSearchQueryKeyWithFilters(props: {
	searchType: SearchOptionType;
	filters?: SieveFilterModel[];
	sortColumn?: string | null;
	sortDirection?: string | null;
	pageSize?: number;
	page?: number;
}) {
	const query = GetSearchProps(props.searchType);
	const sortColumn = props.sortColumn && props.sortColumn !== '' ? props.sortColumn : query.defaultSortColumn;
	const sortDirection = props.sortDirection ? props.sortDirection : query.defaultSortDirection ? query.defaultSortDirection : 'asc';
	const queryString = Sieve.buildSieveUrl(
		props.filters ?? [],
		sortColumn ?? query.defaultSortColumn,
		sortDirection ?? query.defaultSortDirection ?? 'asc',
		props.pageSize,
		props.page
	);
	return {
		queryKey: [query.endpoint, 'search', queryString],
		url: `${query.endpoint}?${queryString}`,
		sortColumn,
		sortDirection
	};
}

export function useSearch<K extends SearchOptionType>(props: useSearchProps<K>): useSearchResult<SearchTypeToModel[K]> {
	const { queryKey, url, sortColumn, sortDirection } = getSearchQueryKeyWithFilters(props);

	const { data, isLoading, isError, error, refetch, isFetching } = useQuery<{ collection: SearchTypeToModel[K][]; count: number }>({
		queryFn: async () => {
			const resp = await axios.get(url);
			if (props.onSuccess) {
				props.onSuccess(resp.data);
			}
			return resp.data;
		},
		queryKey,
		enabled: props.enabled,
		...props
	});

	if (isLoading || isError) {
		return { data: [], count: 0, isLoading, isError, error, refetch, isFetching };
	}

	const count = data?.count ?? 0;

	return {
		data: count > 0 ? data!.collection : [],
		count,
		isLoading,
		isError,
		error,
		refetch,
		isFetching,
		sortColumn,
		sortDirection
	};
}
